import { useLocation, useNavigate } from 'react-router-dom';
import { ROLE_LABEL, adminFacilities, canSee, pathForView, viewForPath, type Role, type ViewId } from '@aithora/shared';
import { useQuery } from '../useQuery';
import { useSession } from '../session/SessionContext';
import { sx } from '../ui/designStyles';

/**
 * View as (design roleGroups, T205-221 and L40426), inside the account menu the role chip
 * opens (shell/AccountMenu): only a Super Admin gets these rows. Picking a role renders
 * the console as that role, read only; picking Super Admin goes back. The session's role
 * never changes, and a reload is always back to your own role.
 */

/** The design's picker, in its order and grouping, as stored roles. */
const GROUPS: { name: string; items: { role: Role; name: string; badge: string; fg: string; bg: string }[] }[] = [
  {
    name: 'Facility',
    items: [
      { role: 'RESIDENT', name: 'Resident', badge: 'RESIDENT', fg: '#1F6B52', bg: 'rgba(47,138,111,.14)' },
      { role: 'FAMILY_MEMBER', name: 'Family', badge: 'FAMILY', fg: '#2F5C97', bg: 'rgba(79,138,214,.14)' },
      { role: 'CAREGIVER', name: 'Care Staff', badge: '', fg: '', bg: '' },
      { role: 'ADMIN', name: 'Facility Admin', badge: '', fg: '', bg: '' },
    ],
  },
  {
    name: 'Aithora',
    items: [
      { role: 'SUPER_ADMIN', name: 'Super Admin', badge: 'ALL ACCESS', fg: '#5B6B7C', bg: 'rgba(91,107,124,.16)' },
      { role: 'OPERATOR', name: 'Operator', badge: 'INTERNAL', fg: '#5B6B7C', bg: 'rgba(91,107,124,.16)' },
      // The server views as any stored role (core/view-as VIEW_AS_ROLES), Partner included.
      { role: 'PARTNER', name: 'Partner', badge: 'READ ONLY', fg: '#5B6B7C', bg: 'rgba(91,107,124,.16)' },
      { role: 'SALES', name: 'Sales', badge: 'INTERNAL', fg: '#5B6B7C', bg: 'rgba(91,107,124,.16)' },
      { role: 'DEVELOPER', name: 'Dev', badge: 'INTERNAL', fg: '#5B6B7C', bg: 'rgba(91,107,124,.16)' },
      { role: 'MEMBER', name: 'Aithora Home', badge: 'AT HOME', fg: '#2F5C97', bg: 'rgba(79,138,214,.14)' },
    ],
  },
];

/** Where each role lands when the page you are on is not one of theirs (design HOME). */
const HOME: Partial<Record<Role, ViewId>> = {
  RESIDENT: 'companion', FAMILY_MEMBER: 'family', MEMBER: 'home', CAREGIVER: 'worklist', ADMIN: 'overview', SALES: 'sales', DEVELOPER: 'pulse',
};

/** The design's picker styles (T206-215, L40439-40464; V9 sizes, L739-756), shared with the account menu. */
export const MENU = 'position:absolute;top:38px;right:0;z-index:40;min-width:310px;background:#FCFAF4;border:1px solid rgba(34,39,46,.14);border-radius:10px;box-shadow:0 14px 34px rgba(34,39,46,.16);padding:6px;display:flex;flex-direction:column;';
export const GROUP = 'font-size:14px;letter-spacing:.16em;text-transform:uppercase;color:#54606C;font-weight:600;padding:9px 14px 5px;';
export const item = (on: boolean) => 'display:flex;align-items:center;gap:11px;flex-wrap:wrap;row-gap:6px;padding:9px 14px;font-size:16px;text-decoration:none;color:#22272E;' + (on ? 'font-weight:700;' : '');
const dot = (on: boolean) => 'width:7px;height:7px;border-radius:50%;flex:0 0 7px;background:' + (on ? '#1F6B52' : '#C6C0B4') + ';';
const badge = (fg: string, bg: string) =>
  fg ? 'display:inline-block;white-space:nowrap;font-variant-numeric:tabular-nums;font-size:14px;font-weight:700;letter-spacing:.08em;border-radius:4px;padding:1px 6px;flex:0 0 auto;color:' + fg + ';background:' + bg + ';' : 'display:none;';

/** The View as rows of the account menu. Picking one closes the menu (`onPicked`). */
/** A building's roles are viewed in one building: the console asks which. */
const FACILITY_ROLES = new Set<Role>(['RESIDENT', 'FAMILY_MEMBER', 'CAREGIVER', 'ADMIN']);

export function ViewAsGroups({ viewingAs, onPicked }: { viewingAs: Role | null; onPicked: () => void }) {
  const { setViewAs } = useSession();
  const navigate = useNavigate();
  const location = useLocation();
  const shown: Role = viewingAs ?? 'SUPER_ADMIN';

  // Picking a role opens it straight away (Andrew 2026-10-23); a building role's building
  // is chosen on the page, in the View as bar (ViewAsBanner), not in this menu.
  const pick = (r: Role) => {
    onPicked();
    setViewAs(r === 'SUPER_ADMIN' ? null : r, null);
    // Stay on this page when the role opens it; otherwise that role's own landing page.
    const here = viewForPath(location.pathname);
    if (!here || !canSee(r, here)) {
      const home = HOME[r] ?? 'overview';
      navigate(pathForView(canSee(r, home) ? home : 'help'));
    }
  };

  return (
    <>
      {GROUPS.map((g) => (
        <div key={g.name} role="group" aria-label={`View as: ${g.name}`} style={sx('display:flex;flex-direction:column;')}>
          <div style={sx(GROUP)}>{g.name}</div>
          {g.items.map((it) => {
            const on = it.role === shown;
            return (
              <div key={it.role} style={sx('display:flex;flex-direction:column;')}>
                <button type="button" role="menuitemradio" aria-checked={on} className="dtab viewas__item" style={sx(item(on))} onClick={() => pick(it.role)}>
                  <span style={sx(dot(on))} aria-hidden="true" />
                  <span>{it.name}</span>
                  <span style={sx('flex:1;')} />
                  <span style={sx(badge(it.fg, it.bg))}>{it.badge}</span>
                </button>
              </div>
            );
          })}
        </div>
      ))}
    </>
  );
}

/** The line across the top of every page while viewing as another role. */
export function ViewAsBanner({ viewingAs }: { viewingAs: Role | null }) {
  const { setViewAs, client, viewAsFacility } = useSession();
  const needsBuilding = viewingAs !== null && FACILITY_ROLES.has(viewingAs);
  const buildings = useQuery((signal) => adminFacilities(client, { signal }), [client], { enabled: needsBuilding });
  if (!viewingAs) return null;
  const list = (buildings.data?.facilities ?? []).filter((f) => f.org_type === 'facility');
  return (
    <div role="status" className="viewas-banner">
      <span className="viewas-banner__tag">VIEWING AS</span>
      <span className="viewas-banner__text">
        <span className="viewas-banner__title">{`Viewing as ${ROLE_LABEL[viewingAs]}`}</span>
        <span className="viewas-banner__note">Read only. Nothing you do here is sent, and your own role is unchanged.</span>
      </span>
      {needsBuilding && (
        <label className="viewas-banner__building">
          <span>Building</span>
          <select
            aria-label="Building to view"
            value={viewAsFacility?.id ?? ''}
            onChange={(e) => {
              const f = list.find((x) => x.id === e.target.value);
              if (f) setViewAs(viewingAs, { id: f.id, name: f.name });
            }}
          >
            {!viewAsFacility && <option value="">Choose a building</option>}
            {list.map((f) => (
              <option key={f.id} value={f.id}>{f.name}</option>
            ))}
          </select>
        </label>
      )}
      <button type="button" className="viewas-banner__stop" onClick={() => setViewAs(null)}>
        Back to Super Admin
      </button>
    </div>
  );
}
